"use client";

import { PWAInstallGuide } from "@/components/notifications";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "@/i18n/routing";
import { VACATION_PROTECTION_DAYS } from "@/lib/constants";
import { Tournament } from "@/types";
import { Button, Card, CardBody, CardHeader, Divider } from "@heroui/react";
import { Icon } from "@iconify/react";
import { formatInTimeZone } from "date-fns-tz";
import { useTranslations } from "next-intl";

interface TournamentWelcomeClientProps {
  tournament: Tournament;
  organizerName: string;
  teamCount: number;
  playerCount: number;
  matchCount: number;
  completedMatchCount: number;
}

export function TournamentWelcomeClient({
  tournament,
  organizerName,
  teamCount,
  playerCount,
  matchCount,
  completedMatchCount,
}: TournamentWelcomeClientProps) {
  const t = useTranslations();
  const { user } = useAuth();

  const hasJokerCards = tournament.jokerCards > 0;
  const hasBraveheartCards = tournament.braveheartCards > 0;

  return (
    <div className="flex flex-col gap-10 md:gap-14 w-full pb-16">
      {/* ============================================================== */}
      {/* TOURNAMENT SPECIFIC SECTION */}
      {/* ============================================================== */}

      {/* Tournament Specific Settings */}
      <section>
        <div className="flex flex-col gap-6">
          <SectionTitle>{t("about.tournamentSettings.title")}</SectionTitle>

          {tournament.description && (
            <Card className="bg-content1/80 backdrop-blur-sm border border-default-200/50">
              <CardBody className="gap-6 p-5 md:p-8">
                <p className="whitespace-pre-wrap">{tournament.description}</p>
              </CardBody>
            </Card>
          )}

          <Card className="bg-content1/80 backdrop-blur-sm border border-default-200/50">
            <CardHeader className="flex items-center gap-3 px-5 pt-5 pb-0 md:px-8 md:pt-8">
              <div className="p-2 rounded-lg bg-[var(--gold)]/10 shrink-0">
                <Icon icon="solar:buildings-3-bold" className="text-lg text-[var(--gold)]" />
              </div>
              <div className="min-w-0">
                <p className="text-default-500 text-xs uppercase tracking-wide">
                  {t("about.tournamentSettings.organizer")}
                </p>
                <p className="font-semibold text-lg text-[var(--text-primary)]">
                  {organizerName}
                </p>
              </div>
            </CardHeader>
            <CardBody className="gap-6 p-5 md:p-8">
              {/* Tournament Info */}
              <div className="grid md:grid-cols-2 gap-5 md:gap-6">
                <InfoItem
                  icon="solar:calendar-bold"
                  label={t("about.tournamentSettings.duration")}
                  value={`${formatInTimeZone(tournament.startsAt, "Europe/Helsinki", "dd.MM.yyyy HH:mm")} - ${formatInTimeZone(
                    tournament.endsAt,
                    "Europe/Helsinki",
                    "dd.MM.yyyy HH:mm",
                  )}`}
                />
                <InfoItem
                  icon="solar:map-point-linear"
                  label={t("about.tournamentSettings.locationCount")}
                  value={String(tournament.locationIds.length)}
                />
                <InfoItem
                  icon="solar:users-group-rounded-bold"
                  label={t("about.tournamentSettings.teamsPerLevel")}
                  value={String(tournament.towerTeamsPerRow)}
                />
                <InfoItem
                  icon="solar:users-group-rounded-bold"
                  label={t("about.tournamentSettings.topRowTeams")}
                  value={String(tournament.towerTopRowTeamCount)}
                />
              </div>

              <Divider className="my-2" />

              {/* Rules */}
              <div>
                <SubSectionTitle icon="solar:document-text-bold">
                  {t("about.tournamentSettings.rules")}
                </SubSectionTitle>
                <div className="grid md:grid-cols-2 gap-3 md:gap-4">
                  <RuleItem
                    label={t("about.tournamentSettings.challengeExpiry")}
                    value={`${tournament.challengeExpiryDays} ${t(
                      "common.days",
                    )}`}
                    description={t(
                      "about.tournamentSettings.challengeExpiryDescription",
                    )}
                  />
                  <RuleItem
                    label={t("about.tournamentSettings.challengeCooldownHours")}
                    value={
                      String(tournament.challengeCooldownHours) +
                      " " +
                      t("common.hours")
                    }
                    description={t(
                      "about.tournamentSettings.challengeCooldownDescription",
                    )}
                  />
                  {tournament.defendCooldownHours > 0 && (
                    <RuleItem
                      label={t(
                        "about.tournamentSettings.defendCooldownHours",
                      )}
                      value={
                        String(tournament.defendCooldownHours) +
                        " " +
                        t("common.hours")
                      }
                      description={t(
                        "about.tournamentSettings.defendCooldownDescription",
                      )}
                    />
                  )}
                  <RuleItem
                    label={t("about.tournamentSettings.maxVacation")}
                    value={`${tournament.maxVacationPeriods} x 7 ${t("common.days")}`}
                    description={t(
                      "about.tournamentSettings.vacationDescription",
                    )}
                  />
                  <RuleItem
                    label={t("about.tournamentSettings.vacationProtection")}
                    value={`${tournament.vacationProtectionDays ?? VACATION_PROTECTION_DAYS} ${t("common.days")}`}
                    description={t(
                      "about.tournamentSettings.vacationProtectionDescription",
                    )}
                  />
                  {/* Special Cards */}
                  {(hasJokerCards || hasBraveheartCards) && (
                    <>
                      {hasJokerCards && (
                        <RuleItem
                          label={t("common.jokerCards")}
                          value={String(tournament.jokerCards)}
                          description={t(
                            "about.tournamentSettings.jokerDescription",
                          )}
                        />
                      )}
                      {hasBraveheartCards && (
                        <>
                          <RuleItem
                            label={t("common.braveheartCards")}
                            value={String(tournament.braveheartCards)}
                            description={t(
                              "about.tournamentSettings.braveheartDescription",
                            )}
                          />
                          <RuleItem
                            label={t(
                              "about.tournamentSettings.braveheartResponseHours",
                            )}
                            value={
                              String(
                                tournament.braveheartResponseHours ?? 48,
                              ) +
                              " " +
                              t("common.hours")
                            }
                            description={t(
                              "about.tournamentSettings.braveheartResponseDescription",
                            )}
                          />
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      </section>

      {/* Statistics Cards */}
      <section>
        <SectionTitle>{t("about.currentStatus")}</SectionTitle>
        <div className="flex flex-col justify-center sm:flex-row gap-4 md:gap-5">
          <StatCard
            icon="solar:users-group-rounded-bold"
            value={teamCount}
            label={t("statistics.teams", { count: teamCount })}
            iconColor="text-amber-400"
            bgColor="bg-amber-500/10"
          />
          <StatCard
            icon="solar:user-bold"
            value={playerCount}
            label={t("statistics.players", { count: playerCount })}
            iconColor="text-sky-400"
            bgColor="bg-sky-500/10"
          />
        </div>
      </section>

      {/* ============================================================== */}
      {/* GENERIC SECTION - How Tower Challenger Works */}
      {/* ============================================================== */}

      {/* How Tower Challenger Works */}
      <section className="pt-2">
        <SectionTitle>{t("about.howItWorks.title")}</SectionTitle>
        <div className="grid md:grid-cols-2 gap-4 md:gap-5">
          <HowItWorksCard
            icon="game-icons:stone-tower"
            title={t("about.howItWorks.towerStructure.title")}
            description={t("about.howItWorks.towerStructure.description")}
            iconColor="text-amber-500"
          />
          <HowItWorksCard
            icon="solar:target-bold"
            title={t("about.howItWorks.challenge.title")}
            description={t("about.howItWorks.challenge.description")}
            iconColor="text-emerald-500"
          />
          <HowItWorksCard
            icon="solar:ranking-bold"
            title={t("about.howItWorks.climb.title")}
            description={t("about.howItWorks.climb.description")}
            iconColor="text-sky-500"
          />
          <HowItWorksCard
            icon="solar:calendar-bold"
            title={t("about.howItWorks.schedule.title")}
            description={t("about.howItWorks.schedule.description")}
            iconColor="text-violet-500"
          />
        </div>
      </section>

      {/* ============================================================== */}
      {/* INSTALL APP SECTION */}
      {/* ============================================================== */}

      <section>
        <SectionTitle>{t("pwaInstall.title")}</SectionTitle>
        <PWAInstallGuide className="max-w-2xl mx-auto" />
      </section>

      {/* ============================================================== */}
      {/* CALL TO ACTION */}
      {/* ============================================================== */}

      {/* Final CTA */}
      <section className="text-center py-8 md:py-12">
        <div className="relative inline-block mb-6">
          <h2 className="text-2xl md:text-3xl font-bold font-cinzel tracking-wide bg-gradient-to-r from-[var(--text-primary)] to-[var(--gold)] bg-clip-text text-transparent">
            {t("about.readyToJoin")}
          </h2>
          <div className="absolute -bottom-2 left-1/2 h-0.5 w-24 -translate-x-1/2 bg-gradient-to-r from-transparent via-[var(--gold)] to-transparent" />
        </div>
        <p className="text-default-500 mb-8 max-w-lg mx-auto text-sm md:text-base">
          {t("about.readyToJoinDescription")}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            as={Link}
            href={`/tournaments/${tournament.slug}`}
            color="primary"
            size="lg"
            className="font-bold px-8 shadow-lg shadow-primary/25"
            startContent={
              <Icon icon="game-icons:stone-tower" className="text-xl" />
            }
          >
            {t("about.viewTournament")}
          </Button>
          {!user && (
            <Button
              as={Link}
              href="/register"
              variant="bordered"
              size="lg"
              className="font-bold px-8"
              startContent={
                <Icon icon="solar:user-plus-bold" className="text-xl" />
              }
            >
              {t("about.createAccount")}
            </Button>
          )}
        </div>
      </section>
    </div>
  );
}

// ============================================================================
// Section Title Component with decorative elements
// ============================================================================
function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative text-center mb-8 md:mb-10">
      <h2 className="text-xl md:text-2xl font-bold font-cinzel tracking-wide text-[var(--text-primary)]">
        {children}
      </h2>
      <div className="absolute left-1/2 -bottom-3 h-0.5 w-16 md:w-20 -translate-x-1/2 bg-gradient-to-r from-transparent via-[var(--gold)] to-transparent" />
    </div>
  );
}

// ============================================================================
// Sub-section Title Component
// ============================================================================
function SubSectionTitle({
  children,
  icon,
}: {
  children: React.ReactNode;
  icon: string;
}) {
  return (
    <h3 className="font-semibold text-base md:text-lg mb-4 flex items-center gap-2 text-[var(--text-primary)]">
      <Icon icon={icon} className="text-[var(--gold)]" />
      {children}
    </h3>
  );
}

// ============================================================================
// Statistics Card Component
// ============================================================================
function StatCard({
  icon,
  value,
  label,
  iconColor,
  bgColor,
}: {
  icon: string;
  value: number;
  label: string;
  iconColor: string;
  bgColor: string;
}) {
  return (
    <Card className="bg-content1/80 backdrop-blur-sm border border-default-200/50 min-w-[200px]">
      <CardBody className="text-center py-5 md:py-6 px-3">
        <div
          className={`mx-auto mb-3 w-12 h-12 md:w-14 md:h-14 rounded-xl ${bgColor} flex items-center justify-center`}
        >
          <Icon icon={icon} className={`text-2xl md:text-3xl ${iconColor}`} />
        </div>
        <p className="text-3xl md:text-4xl font-bold text-[var(--text-primary)]">
          {value}
        </p>
        <p className="text-default-500 text-xs md:text-sm mt-1">{label}</p>
      </CardBody>
    </Card>
  );
}

// ============================================================================
// How It Works Card Component
// ============================================================================
function HowItWorksCard({
  icon,
  title,
  description,
  iconColor,
}: {
  icon: string;
  title: string;
  description: string;
  iconColor: string;
}) {
  return (
    <Card className="bg-content1/80 backdrop-blur-sm border border-default-200/50">
      <CardBody className="flex flex-row gap-4 items-start p-4 md:p-5">
        <div className="p-3 rounded-xl bg-default-100/80 shrink-0">
          <Icon icon={icon} className={`text-2xl ${iconColor}`} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold mb-1.5 text-[var(--text-primary)]">
            {title}
          </h3>
          <p className="text-default-500 text-sm leading-relaxed">
            {description}
          </p>
        </div>
      </CardBody>
    </Card>
  );
}

// ============================================================================
// Info Item Component
// ============================================================================
function InfoItem({
  icon,
  label,
  value,
}: {
  icon: string;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <div className="p-2 rounded-lg bg-[var(--gold)]/10 shrink-0">
        <Icon icon={icon} className="text-lg text-[var(--gold)]" />
      </div>
      <div className="min-w-0">
        <p className="text-default-500 text-xs uppercase tracking-wide">
          {label}
        </p>
        <p className="font-medium text-[var(--text-primary)] truncate">
          {value}
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// Rule Item Component
// ============================================================================
function RuleItem({
  label,
  value,
  description,
}: {
  label: string;
  value: string;
  description?: string;
}) {
  return (
    <div className="flex flex-col p-3 md:p-4 rounded-xl bg-[var(--gold)]/5 dark:bg-[var(--gold)]/[0.03] border border-[var(--gold)]/10 dark:border-[var(--gold)]/10">
      <div className="flex justify-between items-center gap-3">
        <span className="text-default-600 dark:text-[var(--gold)]/80 text-sm">
          {label}
        </span>
        <span className="font-bold text-lg text-[var(--text-primary)] dark:text-[var(--gold)] tabular-nums whitespace-nowrap">
          {value}
        </span>
      </div>
      {description && (
        <p className="text-default-500 dark:text-[var(--gold)]/60 text-xs leading-relaxed mt-2 pt-2 border-t border-[var(--gold)]/10">
          {description}
        </p>
      )}
    </div>
  );
}
